import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DashboardTable from "./DashboardTable";
import TableLoading from "./TableLoading";
import { usePaginatedList } from "../hooks/usePaginatedList";
import { GRID_DEFAULT_PAGE_SIZE } from "../../../constant/constant";
import { withId } from "../utils/drillHelpers";
import { toPlainText } from "../utils/plainText";
import "../reportDashboard.scss";

// Drill-down table behind a "click a bar/row to see the underlying records" action —
// Inspections' form/category/subcategory/project bars, Subcontractors' per-sub
// corrective list. This used to take a caller-fetched `rows` array and hand it to a
// client-paginated DashboardTable behind a hard `.slice(0, 200)` — any drill with more
// than 200 matching records silently lost everything past the cap, with nothing in the
// UI to say rows were missing (the footer just read "1-10 of 200", the array length,
// never the true match count). It now fetches its own page straight from the API via
// `fetchPage`, the same server-pagination contract `usePaginatedList` already drives
// everywhere else in this feature, so a drill with (say) 3,000 corrective items is fully
// reachable a page at a time instead of frozen at its first 200 rows.
//
// `fetchPage({ page, pageSize }) -> Promise<{ rows, totalCount }>` — the caller closes
// over whatever filter (form title / category / involved-party name / etc.) selected
// this drill. `deps` should include that filter plus any org/token/date-range context, so
// switching the selected filter resets back to page 0 instead of showing the previous
// filter's rows under the new title. `idField` names the column GridTable should key rows
// on (it has no natural `id` of its own) — see `withId` in utils/drillHelpers.
//
// Both current call sites render this as `{drill && <DrillModal open .../>}` — the modal
// unmounts entirely once nothing is selected — so it's safe for usePaginatedList below to
// be called unconditionally: there is no "open=false but still mounted, fetchPage
// missing" case that would need a guard clause here.
const DrillModal = ({ open, title, columns, idField, fetchPage, deps, onClose }) => {
  const list = usePaginatedList(fetchPage, deps, { pageSize: GRID_DEFAULT_PAGE_SIZE });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className="rdModal__title">
        <span className="rdModal__titleText">{toPlainText(title)}</span>
        <IconButton className="rdModal__close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Opening a drill always starts with an empty, still-loading list. The
            grid's own MUI overlay only paints over whatever rows it already has,
            so on that first fetch there is nothing to paint over — swap in an
            explicit loader instead, and hand back to the grid (whose `loading`
            prop covers page-to-page moves) once rows exist. */}
        {list.loading && list.rows.length === 0 ? (
          <TableLoading />
        ) : (
          <DashboardTable
            columns={columns}
            rows={withId(list.rows, idField)}
            serverMode
            rowCount={list.totalCount}
            page={list.page}
            onPageChange={list.setPage}
            pageSize={list.pageSize}
            onPageSizeChange={list.setPageSize}
            loading={list.loading}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DrillModal;
